import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Layout from '../../components/Layout';
import DataGrid, { GridColumn, GridAction } from '../../components/DataGrid/DataGrid';
import EmptyState from '../../components/EmptyState';
import TourButton from '../../components/TourButton';
import { usePageTour } from '../../hooks/usePageTour';
import { Product, ProductCategory } from '../../types';
import { productService } from '../../services/productService';
import './ProductCategoryListPage.css';
import { CirclePlus, Eye, Package, RefreshCw, SquarePen, Trash2 } from 'lucide-react';

const ProductListPage: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [products, setProducts] = useState<Product[]>([]);
  const [categories, setCategories] = useState<ProductCategory[]>([]);
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedCategory, setSelectedCategory] = useState<number | null>(null);

  useEffect(() => {
    loadAll();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadAll = async () => {
    setLoading(true);
    setError(null);
    try {
      const [productsRes, categoriesRes] = await Promise.all([
        productService.getProducts({ pageSize: 200 }),
        productService.getCategories({ pageSize: 100 }),
      ]);
      if (productsRes.data.data) {
        const all = (productsRes.data.data as Product[]).filter((p) => !p.isDeleted);
        setProducts(all);
      }
      if (categoriesRes.data.data) {
        setCategories(categoriesRes.data.data as ProductCategory[]);
      }
    } catch (err) {
      setError(t('product.failedToLoadProduct'));
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    setError(null);
    try {
      const [productsRes, categoriesRes] = await Promise.all([
        productService.getProducts({ pageSize: 200 }),
        productService.getCategories({ pageSize: 100 }),
      ]);
      if (productsRes.data.data) {
        const all = (productsRes.data.data as Product[]).filter((p) => !p.isDeleted);
        setProducts(all);
      }
      if (categoriesRes.data.data) {
        setCategories(categoriesRes.data.data as ProductCategory[]);
      }
    } catch (err) {
      setError(t('product.failedToLoadProduct'));
    } finally {
      setRefreshing(false);
    }
  };

  const filteredProducts = selectedCategory
    ? products.filter((p) => p.categoryId === selectedCategory)
    : products;

  const columns: GridColumn[] = [
    {
      key: 'sku',
      label: t('product.sku'),
      sortable: true,
      width: '130px',
      render: (value) => <code>{value}</code>,
    },
    {
      key: 'name',
      label: t('product.productName'),
      sortable: true,
      width: '200px',
      render: (value) => <strong>{value}</strong>,
    },
    {
      key: 'category',
      label: t('product.category'),
      sortable: true,
      width: '160px',
      render: (_value, item) => item.categoryName || 'N/A',
    },
    {
      key: 'unitOfMeasure',
      label: t('product.unitOfMeasure'),
      sortable: true,
      width: '130px',
      hideTablet: true,
    },
    {
      key: 'reorderLevel',
      label: t('product.reorderLevel'),
      sortable: true,
      width: '130px',
      hideMobile: true,
      hideTablet: true,
      render: (value) => `${value} ${t('product.units')}`,
    },
    {
      key: 'createdAt',
      label: t('product.createdDate'),
      sortable: true,
      width: '130px',
      hideMobile: true,
      render: (value) =>
        value
          ? new Date(value).toLocaleDateString(
              t('common.languageCode') === 'ja' ? 'ja-JP' : 'en-US'
            )
          : '',
    },
  ];

  const actions: GridAction[] = [
    {
      icon: (
        <Eye size={16} strokeWidth={1.75} />
      ),
      onClick: (item) => navigate(`/products/${item.id}`),
      className: 'grid-action-btn-view',
      title: t('common.view'),
    },
    {
      icon: (
        <SquarePen size={16} strokeWidth={1.75} />
      ),
      onClick: (item) => navigate(`/products/${item.id}/edit`),
      className: 'grid-action-btn-edit',
      title: t('common.edit'),
    },
    {
      icon: (
        <Trash2 size={16} strokeWidth={1.75} />
      ),
      onClick: (item) => navigate(`/products/${item.id}/delete`),
      className: 'grid-action-btn-delete',
      title: t('common.delete'),
    },
  ];

  const { startTour } = usePageTour('products', [
    {
      element: '[data-tour="product-new"]',
      popover: {
        title: t('tour.products.newTitle') || 'Add products',
        description: t('tour.products.newText')
          || 'Everything you sell starts here. Click to add a product with its price, tax and barcode.',
      },
    },
    {
      element: '[data-tour="product-filter"]',
      popover: {
        title: t('tour.products.filterTitle') || 'Filter by category',
        description: t('tour.products.filterText')
          || 'Narrow the list to one category. Categories are managed under Product Settings.',
      },
    },
    {
      element: '[data-tour="product-grid"]',
      popover: {
        title: t('tour.products.gridTitle') || 'Your product list',
        description: t('tour.products.gridText')
          || 'Search, sort, and use the row buttons to view, edit or delete a product.',
      },
    },
  ], !loading);

  return (
    <Layout>
      <div className="container">
        <div className="header">
          <h1 className="title">{t('product.title')}</h1>
          <div className="header-actions">
            <TourButton onClick={startTour} />
            <select
              className="form-select"
              style={{ width: '180px', fontSize: '0.875rem' }}
              value={selectedCategory ?? ''}
              data-tour="product-filter"
              onChange={(e) =>
                setSelectedCategory(e.target.value ? parseInt(e.target.value) : null)
              }
            >
              <option value="">{t('product.allCategories')}</option>
              {categories.map((cat) => (
                <option key={cat.id} value={cat.id}>
                  {cat.name}
                </option>
              ))}
            </select>
            <button
              className="btn-new"
              data-tour="product-new"
              onClick={() => navigate('/products/create')}
            >
              <CirclePlus/>
              {t('product.newProduct')}
            </button>
            <button
              className="btn-refresh"
              onClick={handleRefresh}
              disabled={refreshing}
              title={t('product.refreshProducts')}
            >
              <RefreshCw size={18} strokeWidth={2} className={refreshing ? 'refresh-icon-spinning' : ''} />
              {refreshing ? t('product.refreshing') : t('product.refreshProducts')}
            </button>
          </div>
        </div>

        {error && (
          <div className="alert alert-danger alert-dismissible fade show">
            {error}
            <button type="button" className="btn-close" onClick={() => setError(null)} />
          </div>
        )}

        <div data-tour="product-grid">
          <DataGrid
            data={filteredProducts}
            columns={columns}
            actions={actions}
            loading={loading}
            pageSize={10}
            searchPlaceholder={t('common.search')}
            emptyMessage={t('product.noProductsFound')}
            emptyState={products.length === 0 ? (
              <EmptyState
                icon={<Package size={44} strokeWidth={1.2} />}
                title={t('emptyState.products.title') || 'No products yet'}
                description={t('emptyState.products.text')
                  || 'Products are the items you buy, stock and sell — each one carries a price, tax and barcode. Add your first product to start selling.'}
                actionLabel={t('product.newProduct')}
                onAction={() => navigate('/products/create')}
              />
            ) : undefined}
            rowsPerPageOptions={[5, 10, 25, 50]}
          />
        </div>
      </div>
    </Layout>
  );
};

export default ProductListPage;
